import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useFeature, PRINT_ORDERING } from '../contexts/ConfigContext';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './OrderListPage.module.css';

/** Where a customer with no orders goes to send film in. */
const STORE_URL = 'https://chemicaldependency.co.uk';

interface Order {
  order_id: string;
  order_number: string;
  order_date: string;
  scan_count: number;
  scan_ready: boolean;
  expired: boolean;
  expired_at?: string;
}

interface OrdersResponse {
  orders: Order[];
  shopify_error: string | null;
}

async function fetchOrders(): Promise<OrdersResponse> {
  const res = await fetch('/api/v1/orders', { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`Failed to fetch orders (${res.status})`);
  }
  return res.json();
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function OrderListPage() {
  usePageTitle('Your orders');
  const printEnabled = useFeature(PRINT_ORDERING);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery<OrdersResponse>({
    queryKey: ['orders'],
    queryFn: fetchOrders,
  });

  const handleRetry = () => {
    queryClient.invalidateQueries({ queryKey: ['orders'] });
  };

  if (isLoading) {
    return (
      <main className={styles.page}>
        <h1 className={styles.heading}>Your Orders</h1>
        <p className={styles.loading}>Loading orders...</p>
      </main>
    );
  }

  if (isError) {
    return (
      <main className={styles.page}>
        <h1 className={styles.heading}>Your Orders</h1>
        <div className={styles.fetchError} role="alert">
          <p>{error instanceof Error ? error.message : 'Something went wrong.'}</p>
          <button className={styles.retryButton} onClick={handleRetry} style={{ marginTop: '1rem' }}>
            Retry
          </button>
        </div>
      </main>
    );
  }

  const orders = data?.orders ?? [];
  const shopifyError = data?.shopify_error ?? null;
  const anyScansReady = orders.some((o) => o.scan_ready);

  return (
    <main className={styles.page}>
      <h1 className={styles.heading}>Your Orders</h1>
      <p className={styles.intro}>
        {printEnabled
          ? "All your orders are listed below. We'll mark an order Ready as soon as its scans are developed. Then you can view them, download them or order prints."
          : "All your orders are listed below. We'll mark an order Ready as soon as its scans are developed and ready to view."}
      </p>

      {shopifyError && (
        <div className={styles.errorBanner} role="alert">
          <span className={styles.errorBannerText}>
            Orders could not be refreshed. Showing cached data.
          </span>
          <button className={styles.retryButton} onClick={handleRetry}>
            Retry
          </button>
        </div>
      )}

      {/* Two genuinely different states. Orders in progress mean scans are
          coming; no orders at all means nothing is on its way, and saying
          "not ready yet" there implies a job that does not exist. */}
      {orders.length > 0 && !anyScansReady && (
        <p className={styles.emptyState}>
          None of your orders have scans ready yet. We'll email you as soon as
          they're developed.
        </p>
      )}

      {orders.length === 0 && (
        <p className={styles.emptyState}>
          No orders yet. Once you send film in to the lab, your order will
          appear here.{' '}
          <a href={STORE_URL} target="_blank" rel="noopener noreferrer">
            Visit the store
          </a>
          .
        </p>
      )}

      {orders.length > 0 && (
        <ul className={styles.orderList}>
          {orders.map((order) => (
            <li key={order.order_id}>
              <Link to={`/orders/${order.order_id}`} className={styles.orderCard}>
                <span
                  className={`${styles.statusDot} ${
                    order.expired
                      ? styles.statusDotExpired
                      : order.scan_ready
                        ? styles.statusDotReady
                        : styles.statusDotPending
                  }`}
                  aria-hidden="true"
                />
                <div className={styles.orderDetails}>
                  <span className={styles.orderNumber}>{order.order_number}</span>
                  <div className={styles.orderMeta}>
                    <span>{formatDate(order.order_date)}</span>
                    <span>{order.scan_count} {order.scan_count === 1 ? 'scan' : 'scans'}</span>
                    <span
                      className={`${styles.statusLabel} ${
                        order.expired
                          ? styles.statusLabelExpired
                          : order.scan_ready
                            ? styles.statusLabelReady
                            : styles.statusLabelPending
                      }`}
                    >
                      {order.expired ? 'Expired' : order.scan_ready ? 'Ready' : 'Pending'}
                    </span>
                    {order.expired && order.expired_at && (
                      <span className={styles.expiredDate}>
                        Expired {formatDate(order.expired_at)}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
